import { Component, inject, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";
import { VisitStatus } from "@app/models/enums/visit-status.enum";
import { BookingReason } from "@app/models/rest/booking-reason";
import { BookingReasonStore } from "@app/services/store/booking-reason-store";
import { AutoBookProfile } from "@app/models/rest/auto-book-profile";
import { AutoBookProfileStore } from "@app/services/store/auto-book-profile-store";
import { ValidationRule } from "@app/models/enums/validation-rule.enum";
import {
  BULK_TRANSFORM_TARGETS,
  BulkTransformTarget,
  EligibilitySummary,
  summariseEligibility,
  VisitLike
} from "./visit-bulk-transform-eligibility";
import { BulkVisitTransformRequest } from "@app/models/dto/bulk-visit-transform-request";

export interface BulkVisitTransformDialogData {
  /** Selected visits from the host grid. Only id and visitStatus are required. */
  visits: VisitLike[];
}

@Component({
  selector: "app-bulk-visit-transform-dialog",
  templateUrl: "./bulk-visit-transform-dialog.component.html",
  styleUrls: ["./bulk-visit-transform-dialog.component.scss"],
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    DialogHeaderComponent
  ]
})
export class BulkVisitTransformDialogComponent implements OnInit {
  dialogRef =
    inject<MatDialogRef<BulkVisitTransformDialogComponent>>(MatDialogRef);
  private fb = inject(UntypedFormBuilder);
  private bookingReasonStore = inject(BookingReasonStore);
  private autoBookProfileStore = inject(AutoBookProfileStore);
  data = inject<BulkVisitTransformDialogData>(MAT_DIALOG_DATA);

  public readonly targets = BULK_TRANSFORM_TARGETS;
  public readonly VisitStatus = VisitStatus;

  public form: UntypedFormGroup;
  public visits: VisitLike[] = [];
  public summary: EligibilitySummary | null = null;
  public allReasons: BookingReason[] = [];
  public validReasons: BookingReason[] = [];
  public profiles: AutoBookProfile[] = [];
  public loaded = false;
  public notesVisible = false;
  public reasonRequired = false;

  ngOnInit(): void {
    this.visits = this.data?.visits ?? [];

    this.form = this.fb.group({
      targetStatus: [null, Validators.required],
      bookingReason: [null],
      notes: [null],
      autoBookProfile: [null]
    });

    Promise.all([
      this.bookingReasonStore.loadAll(null, true),
      this.autoBookProfileStore.loadAll(null, true)
    ]).then(([reasons, profiles]) => {
      this.allReasons = reasons.resources ?? [];
      this.profiles = profiles.resources ?? [];
      this.loaded = true;
    });
  }

  /** Recompute the eligibility preview and reason/profile validators when target changes. */
  onTargetChange(target: BulkTransformTarget): void {
    this.summary = summariseEligibility(this.visits, target);
    this.refreshReasons(target);
    this.refreshValidators(target);
  }

  private refreshReasons(target: BulkTransformTarget): void {
    if (target === VisitStatus.CANCELLED) {
      this.validReasons = this.allReasons.filter((r) => r.cancelBookingReason);
    } else if (target === VisitStatus.REFERRED) {
      this.validReasons = this.allReasons.filter((r) => r.referBookingReason);
    } else {
      this.validReasons = [];
    }
    // Reset selection when target changes so stale reasons don't carry over.
    this.form.controls["bookingReason"].setValue(null);
    this.form.controls["notes"].setValue(null);
    this.notesVisible = false;
  }

  private refreshValidators(target: BulkTransformTarget): void {
    const reasonCtrl = this.form.controls["bookingReason"];
    const notesCtrl = this.form.controls["notes"];
    const profileCtrl = this.form.controls["autoBookProfile"];

    // Reason is required for CANCELLED/REFERRED only when there is at least one valid reason.
    const reasonsApply =
      (target === VisitStatus.CANCELLED || target === VisitStatus.REFERRED) &&
      this.validReasons.length > 0;
    this.reasonRequired = reasonsApply;
    reasonCtrl.setValidators(reasonsApply ? Validators.required : null);
    reasonCtrl.updateValueAndValidity();

    // Notes default to clear; reasonChanged() will re-apply per-reason rules.
    notesCtrl.setValidators(null);
    notesCtrl.updateValueAndValidity();

    // AutoBookProfile is optional override on AUTO; never required.
    profileCtrl.setValidators(null);
    profileCtrl.updateValueAndValidity();
  }

  reasonChanged(reason: BookingReason): void {
    const notesCtrl = this.form.controls["notes"];
    if (!reason) {
      notesCtrl.setValidators(null);
      notesCtrl.updateValueAndValidity();
      this.notesVisible = false;
      return;
    }
    if (reason.noteValidation === ValidationRule.REQUIRED) {
      notesCtrl.setValidators(Validators.required);
      this.notesVisible = true;
    } else if (reason.noteValidation === ValidationRule.OPTIONAL) {
      notesCtrl.setValidators(null);
      this.notesVisible = true;
    } else {
      notesCtrl.setValidators(null);
      this.notesVisible = false;
    }
    notesCtrl.updateValueAndValidity();
  }

  compareById(a: { id: number }, b: { id: number }): boolean {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return a.id === b.id;
  }

  get canSubmit(): boolean {
    return (
      this.form.valid &&
      this.summary != null &&
      this.summary.eligible.length > 0
    );
  }

  onConfirm(): void {
    if (!this.canSubmit) {
      return;
    }
    const target = this.form.controls["targetStatus"].value as VisitStatus;
    const reason = this.form.controls["bookingReason"].value as BookingReason;
    const notes = this.form.controls["notes"].value as string;
    const profile = this.form.controls["autoBookProfile"]
      .value as AutoBookProfile;

    const request: BulkVisitTransformRequest = {
      visitIds: this.summary.eligible.map((v) => v.id),
      targetStatus: target,
      bookingReasonId:
        target === VisitStatus.CANCELLED || target === VisitStatus.REFERRED
          ? reason?.id
          : undefined,
      notes:
        target === VisitStatus.CANCELLED || target === VisitStatus.REFERRED
          ? notes
          : undefined,
      autoBookProfileOverrideId:
        target === VisitStatus.AUTO ? profile?.id : undefined
    };
    this.dialogRef.close(request);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
